import classNames from 'classnames'
import { GetServerSideProps } from 'next'
import { useEffect, useState } from 'react'
import { Alert, Button } from 'react-bootstrap'
import BSCard from 'react-bootstrap/Card'
import Spinner from 'react-bootstrap/Spinner'
import reactStringReplace from 'react-string-replace'

import CSBIcon from '@/assets/csb.svg'
import Card from '@/components/Card/Card'
import MultiImageBanner from '@/components/Combo/MultiImageBanner/MultiImageBanner'
import StepBlocks from '@/components/Combo/StepBlocks/StepBlocks'
import StyledList from '@/components/Combo/StyledList/StyledList'
import VoteButtonGroup, { VOTE_STEP_INDEX } from '@/components/Combo/VoteButtonGroup/VoteButtonGroup'
import VoteInfo from '@/components/Combo/VoteInfo/VoteInfo'
import Container from '@/components/Container/Container'
import { ExplicitHead } from '@/components/DefaultHead/DefaultHead'
import Icon from '@/components/Icon/Icon'
import Main from '@/components/Main/Main'
import Mana, { ManaType } from '@/components/Mana/Mana'
import OutboundLink from '@/components/OutboundLink/OutboundLink'
import cardService from '@/services/card.service'
import voteService from '@/services/vote.service'
import { CardType } from '@/types/card'
import { ComboVoteSubmission, ComboWithVoteData } from '@/types/combo'
import { formatResult, getPrerequisiteList, ZONE_TO_ICON_MAP } from '@/utils/combos'
import { userIsBanned } from '@/utils/helpers.server'
import { artCrop } from '@/utils/images'

import styles from './combo-vote.module.scss'

const BLUESKY_DONE_MESSAGE =
  "I just finished voting on every single combo in EDHREC's combo vote! Vote here: https://edhrec.com/combo-vote #combovote"

export const COMBO_VOTE_IS_FINISHED = true

const format = (input: string) =>
  reactStringReplace(input, /\{([^}]+)\}/, (match) => <Mana color={match as ManaType} />)

type Props = {
  initCountry?: string
  initRegion?: string
}

const ComboVote = () => {
  const [currentCombo, setCurrentCombo] = useState<ComboWithVoteData>()
  const [currentCards, setCurrentCards] = useState<CardType[]>()
  const [previousCombo, setPreviousCombo] = useState<ComboWithVoteData>()
  const [previousCards, setPreviousCards] = useState<CardType[]>()
  const [currentVoteNumber, setCurrentVoteNumber] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(true)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [voteMenuIndex, setVoteMenuIndex] = useState<number>(0)
  const [done, setDone] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (COMBO_VOTE_IS_FINISHED) return
    fetchCombo(0)
  }, [])

  const fetchCombo = async (n: number) => {
    setLoading(true)
    setError(undefined)
    setDone(false)
    const response = await voteService.fetchComboVote(n)
    if (!response) {
      setLoading(false)
      return
    }
    if ('error' in response) {
      if (response.error == 'No more combos.') setDone(true)
      else setError(response.error)
      return
    }

    const cardNames = response.uses.map((use: any) => use.card.name)
    const cardMap = await cardService.getCardMap(cardNames)
    for (const card in cardMap) {
      cardMap[card].url = undefined
    }
    setCurrentCards(Object.values(cardMap).filter((card) => (card as any)?.code)) // Filter out cards without an id - eg error {code: 404, message: "Forbidden"}
    setCurrentCombo(response)
    setCurrentVoteNumber(n + 1)
    setLoading(false)
  }

  const handleSubmit = async (vote: ComboVoteSubmission) => {
    if (!currentCombo) return
    setSubmitting(true)
    await voteService.submitComboVote(currentCombo.vote, vote).catch((e) => console.error(e))
    const combo = currentCombo
    const cards = currentCards
    setVoteMenuIndex(0)
    await fetchCombo(currentVoteNumber)
    setPreviousCombo(combo)
    setPreviousCards(cards)
    setSubmitting(false)
    window.scrollTo(0, 0)
  }

  const handleVoteIndexChange = (index: number) => {
    if (index !== voteMenuIndex) {
      if (index === VOTE_STEP_INDEX.EASY_PREREQ || index === VOTE_STEP_INDEX.EARLY_GAME) {
        const prereqInfo = document.getElementById('prereqInfo')
        if (
          prereqInfo &&
          (prereqInfo.getBoundingClientRect().top < 0 ||
            prereqInfo.getBoundingClientRect().top > window.innerHeight ||
            window.innerWidth <= 800)
        ) {
          prereqInfo.scrollIntoView({ behavior: 'smooth' })
        }
      }
      if (index === VOTE_STEP_INDEX.SIGNIFICANT_RESULT) {
        const resultsInfo = document.getElementById('resultsInfo')
        if (
          resultsInfo &&
          (resultsInfo.getBoundingClientRect().top < 0 ||
            resultsInfo.getBoundingClientRect().top > window.innerHeight ||
            window.innerWidth <= 800)
        ) {
          resultsInfo.scrollIntoView({ behavior: 'smooth' })
        }
      }
    }
    setVoteMenuIndex(index)
  }

  const banner = currentCards ? <MultiImageBanner images={currentCards.map((card) => artCrop(card))} /> : undefined

  return (
    <Main banner={banner}>
      <ExplicitHead
        description='Vote on the bracket relevant two-card combos in EDH.'
        keywords='EDHREC,combo,vote'
        title='Combo Vote | EDHREC'
      />
      <div data-blocklist-universal-player-mobile='1' id='mediavine-settings' />
      <Container>
        {error && (
          <Alert variant='danger'>
            <h2>Error fetching a combo</h2>
            <br />
            <p>
              <b>{error}</b>
            </p>
          </Alert>
        )}

        {COMBO_VOTE_IS_FINISHED && (
          <BSCard className={styles.finished}>
            <h2 className={styles.header}>Combo Vote April 2026</h2>
            <h5>Voting is Closed!</h5>
            <p className='m-0'>
              Thanks for participating in EDHREC&#39;s Bracket Combo Vote. Voting is closed and we are now tallying the
              results. Stay tuned in the coming weeks for combo data on EDHREC.
            </p>
          </BSCard>
        )}

        {done && (
          <Alert variant='success'>
            <h2>That&#39;s all the combos we have for now!</h2>
            <br />
            <p>
              Thanks for voting in EDHREC&#39;s combo vote and especially for voting on every single combo! Stay tuned
              in the coming weeks for the results of the vote.
            </p>
            <OutboundLink url={`https://bsky.app/intent/compose?text=${encodeURIComponent(BLUESKY_DONE_MESSAGE)}`}>
              <Button>
                <Icon icon='bluesky' /> Share on Bluesky
              </Button>
            </OutboundLink>
          </Alert>
        )}

        {!error &&
          !done &&
          !COMBO_VOTE_IS_FINISHED &&
          (!currentCombo || !currentCards || loading ? (
            <div className='align-items-center d-flex flex-column justify-content-center'>
              <Spinner animation='border' variant='primary' />
              <p>
                <i>Fetching a combo for you...</i>
              </p>
            </div>
          ) : (
            <>
              <div className='d-flex gap-2 justify-content-center mb-2'>
                {currentCards.map((card, i) => (
                  <div className={styles.card} key={i}>
                    <Card card={card} scalable />
                  </div>
                ))}
              </div>
              <div>
                <StepBlocks
                  steps={[
                    {
                      className:
                        voteMenuIndex === VOTE_STEP_INDEX.EASY_PREREQ || voteMenuIndex === VOTE_STEP_INDEX.EARLY_GAME
                          ? styles.focused
                          : undefined,
                      node: (
                        <div className='p-2'>
                          <h2 className={styles.header} id='prereqInfo'>
                            <Icon icon='listCheck' />
                            &nbsp;&nbsp;&nbsp;Prerequisites
                          </h2>
                          <StyledList
                            groupIcon='check'
                            items={getPrerequisiteList(currentCombo).map((prereq) => ({
                              icon: prereq.zones.length > 0 ? ZONE_TO_ICON_MAP[prereq.zones[0]] : undefined,
                              item: (
                                <span
                                  className={classNames(
                                    ((!!prereq.zones.length && voteMenuIndex === VOTE_STEP_INDEX.EASY_PREREQ) ||
                                      (!prereq.zones.length && voteMenuIndex === VOTE_STEP_INDEX.EARLY_GAME)) &&
                                      styles.greyedOut,
                                  )}
                                >
                                  {format(prereq.description)}
                                </span>
                              ),
                            }))}
                          />
                        </div>
                      ),
                    },
                    {
                      node: (
                        <div className='p-2'>
                          <h2 className={styles.header}>
                            <Icon icon='route' />
                            &nbsp;&nbsp;&nbsp;Steps
                          </h2>
                          <StyledList items={currentCombo.description.split('\n').map((line: any) => format(line))} />
                        </div>
                      ),
                    },
                    {
                      className: voteMenuIndex === VOTE_STEP_INDEX.SIGNIFICANT_RESULT ? styles.focused : undefined,
                      node: (
                        <div className='p-2'>
                          <h2 className={styles.header} id='resultsInfo'>
                            <Icon icon='infinity' />
                            &nbsp;&nbsp;&nbsp;Results
                          </h2>
                          <StyledList
                            groupIcon='star'
                            items={currentCombo.produces.map((result: any) => ({
                              icon: formatResult(result.feature.name).toLowerCase().includes('infinite')
                                ? 'infinity'
                                : 'star',
                              item: format(formatResult(result.feature.name)),
                            }))}
                          />
                        </div>
                      ),
                    },
                  ]}
                />
              </div>

              <div className='d-flex justify-content-center m-3 gap-3'>
                <OutboundLink url={`https://commanderspellbook.com/combo/${currentCombo.id}`}>
                  <Button className={styles.csb} variant='outline'>
                    <img alt='Spellbook Logo' height='20' src={CSBIcon.src} /> View on Commander Spellbook
                  </Button>
                </OutboundLink>
                <OutboundLink url={`https://commanderspellbook.com/submit-an-update/?comboId=${currentCombo.id}`}>
                  <Button variant='outline-danger'>Report Error</Button>
                </OutboundLink>
              </div>

              <div className='d-flex justify-content-center'>
                <VoteInfo currentCombo={currentCombo} previousCards={previousCards} previousCombo={previousCombo} />
              </div>

              <VoteButtonGroup
                index={voteMenuIndex}
                key={currentCombo.id}
                loading={submitting}
                onIndexChange={handleVoteIndexChange}
                onSubmit={handleSubmit}
              />
            </>
          ))}
      </Container>
    </Main>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async ({ req }) => {
  if (userIsBanned(req.headers)) return { redirect: { destination: '/access-restricted', permanent: false } }
  const props: Props = {}
  if (req.headers['cloudfront-viewer-country']) props.initCountry = req.headers['cloudfront-viewer-country'] as string
  if (req.headers['cloudfront-viewer-country-region'])
    props.initRegion = req.headers['cloudfront-viewer-country-region'] as string
  return { props }
}

export default ComboVote
